/**
 * Security Dashboard Component (DC-637)
 *
 * Admin dashboard for security monitoring and compliance.
 * Displays security metrics, alerts, and activity patterns.
 *
 * @version 1.0.0
 * @date 2026-01-03
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import './security-dashboard.css';

// ============================================================================
// TYPES
// ============================================================================

interface SecurityAlert {
  id: string;
  type: string;
  message: string;
  severity: 'critical' | 'warning' | 'info';
  timestamp: string;
  details?: Record<string, unknown>;
}

interface SecurityDashboardData {
  activeSessions: {
    count: number;
    byDeviceType: Record<string, number>;
  };
  failedLogins: {
    last24h: number;
    last7d: number;
    topOffenders: Array<{ email: string; count: number; lastAttempt: string }>;
  };
  securityAlerts: {
    total: number;
    critical: number;
    warning: number;
    info: number;
    items: SecurityAlert[];
  };
  activityHeatmap: {
    hourly: number[];
    daily: Array<{ date: string; count: number }>;
  };
  complianceStatus: {
    overall: 'compliant' | 'partial' | 'non-compliant' | 'unknown';
    mfaAdoption: number;
    passwordPolicyCompliance: number;
    sessionSecurityScore: number;
  };
  anomalyIndicators: {
    unusualLoginTimes: number;
    newDevices: number;
    failedMfaAttempts: number;
    multipleFailedLogins: number;
  };
  period: {
    start: string;
    end: string;
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAlertType(type: string): string {
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getComplianceColor(status: string): string {
  switch (status) {
    case 'compliant':
      return 'var(--success, #22c55e)';
    case 'partial':
      return 'var(--warning, #f59e0b)';
    case 'non-compliant':
      return 'var(--error, #ef4444)';
    default:
      return 'var(--text-secondary, #666)';
  }
}

function getComplianceLabel(status: string): string {
  switch (status) {
    case 'compliant':
      return 'Compliant';
    case 'partial':
      return 'Partial';
    case 'non-compliant':
      return 'Non-Compliant';
    default:
      return 'Unknown';
  }
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface MetricCardProps {
  icon: string;
  label: string;
  value: string | number;
  sublabel?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  variant?: 'default' | 'warning' | 'error' | 'success';
}

const MetricCard: React.FC<MetricCardProps> = ({
  icon,
  label,
  value,
  sublabel,
  trend,
  trendValue,
  variant = 'default',
}) => {
  return (
    <div className={`metric-card metric-${variant}`}>
      <div className="metric-icon">{icon}</div>
      <div className="metric-content">
        <span className="metric-label">{label}</span>
        <span className="metric-value">{value}</span>
        {sublabel && <span className="metric-sublabel">{sublabel}</span>}
        {trend && trendValue && (
          <span className={`metric-trend trend-${trend}`}>
            {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trendValue}
          </span>
        )}
      </div>
    </div>
  );
};

interface AlertItemProps {
  alert: SecurityAlert;
  onViewDetails: (alert: SecurityAlert) => void;
}

const AlertItem: React.FC<AlertItemProps> = ({ alert, onViewDetails }) => {
  return (
    <div className={`alert-item alert-${alert.severity}`}>
      <div className="alert-icon">
        {alert.severity === 'critical' && '🚨'}
        {alert.severity === 'warning' && '⚠️'}
        {alert.severity === 'info' && 'ℹ️'}
      </div>
      <div className="alert-content">
        <div className="alert-header">
          <span className={`alert-badge badge-${alert.severity}`}>
            {alert.severity.toUpperCase()}
          </span>
          <span className="alert-type">{formatAlertType(alert.type)}</span>
        </div>
        <p className="alert-message">{alert.message}</p>
        <span className="alert-time">{formatDateTime(alert.timestamp)}</span>
      </div>
      <button
        className="btn-icon"
        onClick={() => onViewDetails(alert)}
        title="View details"
      >
        👁️
      </button>
    </div>
  );
};

interface HeatmapBarProps {
  hourly: number[];
}

const HeatmapBar: React.FC<HeatmapBarProps> = ({ hourly }) => {
  const maxValue = Math.max(...hourly, 1);

  return (
    <div className="heatmap-container">
      <div className="heatmap-bars">
        {hourly.map((count, hour) => (
          <div key={hour} className="heatmap-bar-wrapper">
            <div
              className="heatmap-bar"
              style={{
                height: `${(count / maxValue) * 100}%`,
                backgroundColor:
                  count > maxValue * 0.7
                    ? 'var(--primary, #2563eb)'
                    : count > maxValue * 0.3
                    ? 'var(--primary-light, #60a5fa)'
                    : 'var(--bg-hover, #e5e7eb)',
              }}
              title={`${hour}:00 - ${count} events`}
            />
            {hour % 4 === 0 && <span className="heatmap-label">{hour}</span>}
          </div>
        ))}
      </div>
      <div className="heatmap-legend">
        <span>12 AM</span>
        <span>Activity by Hour (Last 7 Days)</span>
        <span>11 PM</span>
      </div>
    </div>
  );
};

interface ComplianceGaugeProps {
  label: string;
  value: number;
}

const ComplianceGauge: React.FC<ComplianceGaugeProps> = ({ label, value }) => {
  const getColor = () => {
    if (value >= 80) return 'var(--success, #22c55e)';
    if (value >= 50) return 'var(--warning, #f59e0b)';
    return 'var(--error, #ef4444)';
  };

  return (
    <div className="compliance-gauge">
      <div className="gauge-label">{label}</div>
      <div className="gauge-bar">
        <div
          className="gauge-fill"
          style={{ width: `${value}%`, backgroundColor: getColor() }}
        />
      </div>
      <div className="gauge-value">{value}%</div>
    </div>
  );
};

interface AlertDetailModalProps {
  alert: SecurityAlert;
  onClose: () => void;
}

const AlertDetailModal: React.FC<AlertDetailModalProps> = ({ alert, onClose }) => {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content alert-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Alert Details</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Severity</span>
              <span className={`detail-value alert-badge badge-${alert.severity}`}>
                {alert.severity.toUpperCase()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value">{formatAlertType(alert.type)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Timestamp</span>
              <span className="detail-value">{formatDateTime(alert.timestamp)}</span>
            </div>
            <div className="detail-row full-width">
              <span className="detail-label">Message</span>
              <span className="detail-value">{alert.message}</span>
            </div>
            {alert.details && Object.keys(alert.details).length > 0 && (
              <div className="detail-row full-width">
                <span className="detail-label">Details</span>
                <pre className="detail-json">
                  {JSON.stringify(alert.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const SecurityDashboard: React.FC = () => {
  const { authFetch } = useAuthenticatedFetch();
  const [data, setData] = useState<SecurityDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAlert, setSelectedAlert] = useState<SecurityAlert | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/security/dashboard');

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch security dashboard');
      }

      const result = await response.json();
      setData(result.data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleRefresh = () => {
    fetchDashboard();
  };

  // Render loading skeleton
  if (loading && !data) {
    return (
      <div className="security-dashboard">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Security Dashboard</h1>
            <p className="header-subtitle">Loading security metrics...</p>
          </div>
        </div>
        <div className="metrics-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="metric-card skeleton-card">
              <div className="skeleton skeleton-icon" />
              <div className="skeleton-content">
                <div className="skeleton skeleton-label" />
                <div className="skeleton skeleton-value" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="security-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Security Dashboard</h1>
          <p className="header-subtitle">
            Monitor security metrics, alerts, and compliance status
          </p>
        </div>
        <div className="header-actions">
          <span className="last-refresh">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? '↻ Refreshing...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <span className="error-icon">!</span>
          <span>{error}</span>
          <button className="error-dismiss" onClick={() => setError(null)}>
            ×
          </button>
        </div>
      )}

      {data && (
        <>
          {/* Metrics Grid */}
          <div className="metrics-grid">
            <MetricCard
              icon="🔐"
              label="Active Sessions"
              value={data.activeSessions.count}
              sublabel={`${data.activeSessions.byDeviceType.desktop || 0} desktop, ${data.activeSessions.byDeviceType.mobile || 0} mobile`}
            />
            <MetricCard
              icon="⚠️"
              label="Failed Logins (24h)"
              value={data.failedLogins.last24h}
              sublabel={`${data.failedLogins.last7d} in last 7 days`}
              variant={data.failedLogins.last24h > 10 ? 'warning' : 'default'}
            />
            <MetricCard
              icon="🚨"
              label="Security Alerts"
              value={data.securityAlerts.total}
              sublabel={`${data.securityAlerts.critical} critical, ${data.securityAlerts.warning} warnings`}
              variant={data.securityAlerts.critical > 0 ? 'error' : data.securityAlerts.warning > 0 ? 'warning' : 'default'}
            />
            <MetricCard
              icon="✓"
              label="Compliance Status"
              value={getComplianceLabel(data.complianceStatus.overall)}
              variant={
                data.complianceStatus.overall === 'compliant'
                  ? 'success'
                  : data.complianceStatus.overall === 'partial'
                  ? 'warning'
                  : 'error'
              }
            />
          </div>

          {/* Two Column Layout */}
          <div className="dashboard-columns">
            {/* Left Column - Alerts */}
            <div className="dashboard-column">
              <div className="section-card">
                <div className="section-header">
                  <h2>Security Alerts</h2>
                  <span className="section-count">{data.securityAlerts.total} total</span>
                </div>
                <div className="alerts-list">
                  {data.securityAlerts.items.length === 0 ? (
                    <div className="empty-state">
                      <span className="empty-icon">✓</span>
                      <p>No security alerts</p>
                      <p className="empty-hint">Your organization is secure</p>
                    </div>
                  ) : (
                    data.securityAlerts.items.map((alert) => (
                      <AlertItem
                        key={alert.id}
                        alert={alert}
                        onViewDetails={setSelectedAlert}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Anomaly Indicators */}
              <div className="section-card">
                <div className="section-header">
                  <h2>Anomaly Indicators</h2>
                </div>
                <div className="anomaly-grid">
                  <div className="anomaly-item">
                    <span className="anomaly-value">{data.anomalyIndicators.unusualLoginTimes}</span>
                    <span className="anomaly-label">Off-Hours Logins</span>
                  </div>
                  <div className="anomaly-item">
                    <span className="anomaly-value">{data.anomalyIndicators.newDevices}</span>
                    <span className="anomaly-label">New Devices</span>
                  </div>
                  <div className="anomaly-item">
                    <span className="anomaly-value">{data.anomalyIndicators.failedMfaAttempts}</span>
                    <span className="anomaly-label">MFA Failures</span>
                  </div>
                  <div className="anomaly-item">
                    <span className="anomaly-value">{data.anomalyIndicators.multipleFailedLogins}</span>
                    <span className="anomaly-label">Accounts at Risk</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Activity & Compliance */}
            <div className="dashboard-column">
              {/* Activity Heatmap */}
              <div className="section-card">
                <div className="section-header">
                  <h2>Activity Pattern</h2>
                  <span className="section-period">
                    {formatDate(data.period.start)} - {formatDate(data.period.end)}
                  </span>
                </div>
                <HeatmapBar hourly={data.activityHeatmap.hourly} />
              </div>

              {/* Compliance Status */}
              <div className="section-card">
                <div className="section-header">
                  <h2>Compliance Metrics</h2>
                  <span
                    className="compliance-badge"
                    style={{ backgroundColor: getComplianceColor(data.complianceStatus.overall) }}
                  >
                    {getComplianceLabel(data.complianceStatus.overall)}
                  </span>
                </div>
                <div className="compliance-gauges">
                  <ComplianceGauge
                    label="MFA Adoption"
                    value={data.complianceStatus.mfaAdoption}
                  />
                  <ComplianceGauge
                    label="Password Policy"
                    value={data.complianceStatus.passwordPolicyCompliance}
                  />
                  <ComplianceGauge
                    label="Session Security"
                    value={data.complianceStatus.sessionSecurityScore}
                  />
                </div>
              </div>

              {/* Top Failed Login Offenders */}
              {data.failedLogins.topOffenders.length > 0 && (
                <div className="section-card">
                  <div className="section-header">
                    <h2>Failed Login Attempts</h2>
                  </div>
                  <div className="offenders-list">
                    {data.failedLogins.topOffenders.slice(0, 5).map((offender, index) => (
                      <div key={index} className="offender-item">
                        <span className="offender-email">{offender.email}</span>
                        <span className="offender-count">{offender.count} attempts</span>
                        <span className="offender-time">
                          Last: {formatDateTime(offender.lastAttempt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <AlertDetailModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
        />
      )}
    </div>
  );
};

export default SecurityDashboard;
